/**
 * Receipt List Component
 *
 * Displays a grid/list of receipt cards with thumbnails and key details.
 * Supports click handlers for editing and optional add button.
 */

import { renderIcon } from '../utils/icons';
import type { Receipt } from '../types/expense-tracker';
import { formatCurrency } from '../types/expense-tracker';

/**
 * Receipt list options
 */
export interface ReceiptListOptions {
  /** Array of receipts to display */
  receipts: Receipt[];
  /** Callback when a receipt is clicked */
  onReceiptClick: (receiptId: string) => void;
  /** Callback when add receipt button is clicked */
  onAddReceipt?: () => void;
  /** Callback when delete receipt button is clicked */
  onDeleteReceipt?: (receiptId: string) => void;
  /** Callback when download receipt button is clicked */
  onDownloadReceipt?: (receiptId: string) => void;
  /** Whether receipts can be edited (shows add button) */
  isEditable: boolean;
  /** Display mode */
  viewMode?: 'grid' | 'list';
}

/**
 * Format receipt date for display
 */
function formatReceiptDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Get extraction status indicator
 */
function getExtractionIndicator(status: string): { icon: string; color: string; label: string } {
  const indicators: Record<string, { icon: string; color: string; label: string }> = {
    COMPLETED: { icon: 'sparkles', color: 'text-green-500', label: 'AI Extracted' },
    PROCESSING: { icon: 'loader', color: 'text-blue-500', label: 'Processing' },
    PENDING: { icon: 'clock', color: 'text-amber-500', label: 'Pending' },
    FAILED: { icon: 'alertTriangle', color: 'text-destructive', label: 'Extraction Failed' },
    MANUAL: { icon: 'edit', color: 'text-muted-foreground', label: 'Manual Entry' },
  };
  return indicators[status] ?? indicators.MANUAL!;
}

/**
 * Create a single receipt card element
 */
function createReceiptCard(
  receipt: Receipt,
  onClick: (receiptId: string) => void,
  onDelete?: (receiptId: string) => void,
  onDownload?: (receiptId: string) => void,
  isEditable: boolean = false
): HTMLElement {
  const card = document.createElement('div');
  card.className = `
    glass-card p-4 rounded-xl cursor-pointer
    hover:bg-muted/50 transition-colors
    border border-border hover:border-primary/30
    relative
  `.replace(/\s+/g, ' ').trim();
  card.setAttribute('data-testid', 'receipt-card');

  card.addEventListener('click', (e) => {
    // Don't trigger card click if clicking the edit, delete or download button
    if ((e.target as HTMLElement).closest('.receipt-edit-btn, .receipt-delete-btn, .receipt-download-btn')) {
      return;
    }
    onClick(receipt.id);
  });

  // Build thumbnail URL
  const thumbnailUrl = `/api/receipts/${receipt.id}/image?thumbnail=true`;

  // Extraction indicator
  const extraction = getExtractionIndicator(receipt.extraction_status);

  card.innerHTML = `
    <div class="absolute top-2 right-2 flex gap-1 z-10">
      <button
        class="receipt-edit-btn p-1.5 rounded-md
               text-muted-foreground hover:text-primary hover:bg-primary/10
               transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        aria-label="Edit receipt"
        data-testid="edit-receipt-btn"
      >
        ${renderIcon('edit', { size: 'sm' }).outerHTML}
      </button>
      ${onDownload ? `
        <button
          class="receipt-download-btn p-1.5 rounded-md
                 text-muted-foreground hover:text-primary hover:bg-primary/10
                 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Download receipt"
          data-testid="download-receipt-btn"
        >
          ${renderIcon('download', { size: 'sm' }).outerHTML}
        </button>
      ` : ''}
      ${isEditable && onDelete ? `
        <button
          class="receipt-delete-btn p-1.5 rounded-md
                 text-muted-foreground hover:text-destructive hover:bg-destructive/10
                 transition-colors focus:outline-none focus:ring-2 focus:ring-destructive"
          aria-label="Delete receipt"
          data-testid="delete-receipt-btn"
        >
          ${renderIcon('trash', { size: 'sm' }).outerHTML}
        </button>
      ` : ''}
    </div>
    <div class="flex gap-4">
      <!-- Thumbnail -->
      <div class="w-20 h-20 rounded-lg bg-muted overflow-hidden shrink-0" data-thumbnail-container>
        <img
          src="${thumbnailUrl}"
          alt="Receipt thumbnail"
          class="w-full h-full object-cover"
          loading="lazy"
          data-thumbnail-image
        />
      </div>

      <!-- Details -->
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2 mb-1">
          <h4 class="font-medium text-foreground truncate">
            ${receipt.vendor || receipt.vendor_name || 'Unknown Vendor'}
          </h4>
          <span class="inline-flex items-center gap-1 text-xs ${extraction.color}">
            ${renderIcon(extraction.icon as Parameters<typeof renderIcon>[0], { size: 'xs' }).outerHTML}
          </span>
        </div>

        <p class="text-sm text-muted-foreground mb-2">
          ${formatReceiptDate(receipt.receipt_date)}
        </p>

        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <span class="font-semibold text-foreground">
              ${formatCurrency(receipt.amount, receipt.currency || 'CHF')}
            </span>
            ${receipt.currency && receipt.currency !== 'CHF' && receipt.amount_chf ? `
              <span class="text-sm text-muted-foreground" title="Rate: ${receipt.conversion_rate ? receipt.conversion_rate.toFixed(4) : 'N/A'}${receipt.conversion_date ? ` (${new Date(receipt.conversion_date).toLocaleDateString('de-CH')})` : ''}">
                (≈ CHF ${receipt.amount_chf.toFixed(2)})
              </span>
            ` : ''}
          </div>
          ${receipt.vat_amount ? `
            <span class="text-xs text-muted-foreground">
              VAT: ${formatCurrency(receipt.vat_amount, 'CHF')}
            </span>
          ` : ''}
        </div>

        ${receipt.expense_type ? `
          <span class="inline-block mt-2 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground" data-testid="expense-type">
            ${receipt.expense_type}
          </span>
        ` : ''}
      </div>
    </div>
  `;

  // Add error handler for thumbnail image
  const thumbnailImg = card.querySelector('[data-thumbnail-image]') as HTMLImageElement;
  const thumbnailContainer = card.querySelector('[data-thumbnail-container]') as HTMLElement;
  if (thumbnailImg && thumbnailContainer) {
    thumbnailImg.addEventListener('error', () => {
      const fallbackDiv = document.createElement('div');
      fallbackDiv.className = 'w-full h-full flex items-center justify-center text-muted-foreground';
      fallbackDiv.appendChild(renderIcon('receipt', { size: 'lg' }));
      thumbnailContainer.innerHTML = '';
      thumbnailContainer.appendChild(fallbackDiv);
    });
  }

  // Add edit button handler
  const editBtn = card.querySelector('.receipt-edit-btn');
  if (editBtn) {
    editBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent card click
      onClick(receipt.id);
    });
  }

  // Add download button handler
  if (onDownload) {
    const downloadBtn = card.querySelector('.receipt-download-btn');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent card click
        onDownload(receipt.id);
      });
    }
  }

  // Add delete button handler
  if (isEditable && onDelete) {
    const deleteBtn = card.querySelector('.receipt-delete-btn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent card click
        onDelete(receipt.id);
      });
    }
  }

  return card;
}

/**
 * Create an add receipt card element
 */
function createAddReceiptCard(onClick: () => void): HTMLElement {
  const card = document.createElement('div');
  card.className = `
    glass-card p-4 rounded-xl cursor-pointer
    hover:bg-muted/50 transition-colors
    border border-dashed border-border hover:border-primary/50
    flex items-center justify-center min-h-[120px]
  `.replace(/\s+/g, ' ').trim();

  card.addEventListener('click', onClick);

  card.innerHTML = `
    <div class="text-center">
      <div class="w-12 h-12 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
        ${renderIcon('plus', { size: 'lg', className: 'text-primary' }).outerHTML}
      </div>
      <span class="text-sm font-medium text-primary">Add Receipt</span>
    </div>
  `;

  return card;
}

/**
 * Create empty state element
 */
function createEmptyState(isEditable: boolean, onAddReceipt?: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-12';

  container.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
      ${renderIcon('receipt', { size: 'xl', className: 'text-muted-foreground' }).outerHTML}
    </div>
    <h3 class="text-lg font-medium text-foreground mb-2">No receipts yet</h3>
    <p class="text-sm text-muted-foreground mb-4">
      ${isEditable ? 'Add receipts to document your expenses' : 'No receipts have been added to this report'}
    </p>
  `;

  if (isEditable && onAddReceipt) {
    const addButton = document.createElement('button');
    addButton.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
    addButton.innerHTML = `
      ${renderIcon('plus', { size: 'sm' }).outerHTML}
      <span>Add Receipt</span>
    `;
    addButton.addEventListener('click', onAddReceipt);
    container.appendChild(addButton);
  }

  return container;
}

/**
 * Create a receipt list component
 */
export function createReceiptList(options: ReceiptListOptions): HTMLElement {
  const {
    receipts,
    onReceiptClick,
    onAddReceipt,
    onDeleteReceipt,
    onDownloadReceipt,
    isEditable,
    viewMode = 'grid',
  } = options;

  const container = document.createElement('div');

  // Empty state
  if (receipts.length === 0) {
    container.appendChild(createEmptyState(isEditable, onAddReceipt));
    return container;
  }

  // Grid/list container
  const gridClasses = viewMode === 'grid'
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
    : 'space-y-4';

  container.className = gridClasses;

  // Add receipt cards
  receipts.forEach((receipt) => {
    const card = createReceiptCard(receipt, onReceiptClick, onDeleteReceipt, onDownloadReceipt, isEditable);
    container.appendChild(card);
  });

  // Add "Add Receipt" card if editable
  if (isEditable && onAddReceipt) {
    const addCard = createAddReceiptCard(onAddReceipt);
    container.appendChild(addCard);
  }

  return container;
}

export default createReceiptList;
